import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useShop } from "@/contexts/ShopContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/KpiCard";
import {
  ExternalLink, Copy, Check, Zap, Clock, Trophy,
  Loader2, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";

const priorityConfig = {
  quick_win: { label: "Quick Win", color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: Zap },
  medium: { label: "Mittel", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Clock },
  high_effort: { label: "Aufwendig", color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/30", icon: Trophy },
};

const statusLabel = {
  todo: "To Do",
  in_progress: "In Arbeit",
  done: "Fertig",
  skipped: "Übersprungen",
};

export default function Backlinks() {
  const { selectedShopId, selectedShop } = useShop();

  const { data, isLoading } = useQuery({
    queryKey: ["backlinks", selectedShopId],
    queryFn: async () =>
      (await api.get(`/shops/${selectedShopId}/backlinks/opportunities`)).data,
    enabled: !!selectedShopId,
  });

  const [filter, setFilter] = useState("all");
  const opportunities = data?.opportunities || [];
  const stats = data?.stats || { total: 0, done: 0, quick_wins_remaining: 0, progress_pct: 0 };

  const filtered = opportunities.filter((o) => {
    if (filter === "all") return true;
    if (filter === "quick_wins") return o.priority === "quick_win";
    if (filter === "todo") return o.status === "todo";
    if (filter === "done") return o.status === "done";
    return true;
  });

  return (
    <div className="space-y-8" data-testid="backlinks-page">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Backlink-Aufbau</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Kuratierte Empfehlungen für {selectedShop?.domain} — Aufgaben mit
          konkreten Prompts und geschätztem Zeitaufwand.
        </p>
      </header>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          title="Opportunities gesamt"
          value={isLoading ? "…" : stats.total.toString()}
          hint="Kuratierte Vorschläge"
        />
        <KpiCard
          title="Erledigt"
          value={isLoading ? "…" : stats.done.toString()}
          hint={`${stats.progress_pct}% Fortschritt`}
        />
        <KpiCard
          title="Quick Wins offen"
          value={isLoading ? "…" : stats.quick_wins_remaining.toString()}
          hint="Unter 30 Min. pro Task"
        />
        <KpiCard
          title="In Arbeit"
          value={isLoading ? "…" : (stats.in_progress ?? 0).toString()}
          hint="Aktuell dran"
        />
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: `Alle · ${opportunities.length}` },
          { key: "quick_wins", label: `⚡ Quick Wins · ${opportunities.filter(o => o.priority === "quick_win").length}` },
          { key: "todo", label: `To Do · ${opportunities.filter(o => o.status === "todo").length}` },
          { key: "done", label: `Fertig · ${opportunities.filter(o => o.status === "done").length}` },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={cn(
              "px-3 py-1 rounded-full text-xs font-mono border transition-colors",
              filter === f.key
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent text-muted-foreground border-border hover:border-foreground/40",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Opportunities List */}
      <div className="space-y-3">
        {isLoading && (
          <div className="text-center text-muted-foreground py-12">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Lädt Opportunities …
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="text-center text-muted-foreground py-12">Keine Einträge</div>
        )}
        {filtered.map((op) => (
          <OpportunityCard key={op.opportunity_id} opportunity={op} shopId={selectedShopId} />
        ))}
      </div>
    </div>
  );
}

function OpportunityCard({ opportunity, shopId }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const qc = useQueryClient();

  const pcfg = priorityConfig[opportunity.priority] || priorityConfig.medium;
  const PIcon = pcfg.icon;

  const statusMutation = useMutation({
    mutationFn: async (status) =>
      (await api.patch(`/shops/${shopId}/backlinks/${opportunity.opportunity_id}`, { status })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backlinks", shopId] });
      toast.success("Status aktualisiert");
    },
  });

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(opportunity.example_prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Prompt kopiert!");
  };

  const isDone = opportunity.status === "done";

  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden transition-colors",
        isDone ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card"
      )}
      data-testid={`opp-${opportunity.opportunity_id}`}
    >
      <div className="p-4 flex items-start gap-4">
        <div className={cn("shrink-0 h-10 w-10 rounded-full grid place-items-center", pcfg.bg)}>
          <PIcon className={cn("h-5 w-5", pcfg.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn("font-semibold", isDone && "line-through text-muted-foreground")}>
                  {opportunity.name}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                  DA {opportunity.domain_authority}
                </span>
                <span className={cn("text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border", pcfg.color, pcfg.border, pcfg.bg)}>
                  {pcfg.label}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">
                  ~{opportunity.effort_minutes} Min
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{opportunity.description}</div>
            </div>
            <select
              value={opportunity.status}
              onChange={(e) => statusMutation.mutate(e.target.value)}
              className="text-xs font-mono bg-transparent border border-border rounded px-2 py-1 shrink-0"
              data-testid={`status-select-${opportunity.opportunity_id}`}
            >
              {Object.entries(statusLabel).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <a href={opportunity.contact_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                Öffnen
              </a>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              className="gap-1.5"
              data-testid={`toggle-prompt-${opportunity.opportunity_id}`}
            >
              <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-90")} />
              {expanded ? "Details schließen" : "Details & Prompt zeigen"}
            </Button>
          </div>

          {expanded && (
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">
                  Was zu tun ist
                </div>
                <div className="text-sm">{opportunity.action}</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                    Copy-Paste Prompt
                  </div>
                  <Button size="sm" variant="ghost" onClick={copyPrompt} className="gap-1.5 h-7">
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Kopiert" : "Kopieren"}
                  </Button>
                </div>
                <pre className="text-xs font-mono whitespace-pre-wrap bg-secondary/60 border border-border rounded p-3 max-h-64 overflow-auto">
                  {opportunity.example_prompt}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
